import { bridgelessRestApiClient } from '@/api/clients'
import { buildPaginationParams } from '@/helpers'
import {
  CoinAmount,
  PaginationParams,
  PaginationResponse,
  Validator,
} from '@/types'

export interface Delegation {
  delegator_address: string
  validator_address: string
  shares: string
  amount: string
  timestamp: string // ISO date-time
}

export interface DelegationsResponse {
  delegation_responses: Array<{
    delegation: Delegation
    balance: CoinAmount
  }>
  pagination: PaginationResponse
}

export async function fetchDelegations(
  delegatorAddress: string,
  pagination?: PaginationParams,
): Promise<DelegationsResponse> {
  const { data } = await bridgelessRestApiClient.get<DelegationsResponse>(
    `/cosmos/staking/v1beta1/delegations/${delegatorAddress}`,
    {
      params: buildPaginationParams(pagination),
    },
  )
  return data
}

export interface StakingParams {
  unbonding_time: string
  max_validators: number
  max_entries: number
  historical_entries: number
  bond_denom: string
  min_commission_rate: string
  minimal_delegation_amount: string
}

export async function fetchStakingParams(): Promise<StakingParams> {
  const { data } = await bridgelessRestApiClient.get<{ params: StakingParams }>(
    '/cosmos/staking/v1beta1/params',
  )
  return data?.params
}

export interface ValidatorsResponse {
  validators: Validator[]
  pagination: PaginationResponse
}

export async function fetchValidatorsList(
  pagination?: PaginationParams,
): Promise<ValidatorsResponse> {
  const { data } = await bridgelessRestApiClient.get<ValidatorsResponse>(
    '/cosmos/staking/v1beta1/validators',
    {
      params: buildPaginationParams(pagination),
    },
  )
  return data
}

export async function fetchDelegatorValidators(
  delegatorAddress: string,
): Promise<string[]> {
  const { data } = await bridgelessRestApiClient.get<{ validators: string[] }>(
    `/cosmos/distribution/v1beta1/delegators/${delegatorAddress}/validators`,
  )

  return data.validators
}

export interface UnbondingDelegationEntry {
  creation_height: string
  completion_time: string // ISO date-time
  initial_balance: string
  balance: string
}

export interface UnbondingDelegation {
  delegator_address: string
  validator_address: string
  entries: UnbondingDelegationEntry[]
}

export interface UnbondingDelegationsResponse {
  unbonding_responses: UnbondingDelegation[]
  pagination: PaginationResponse
}

export async function fetchUnbondingDelegations(
  delegatorAddress: string,
  pagination?: PaginationParams,
): Promise<UnbondingDelegationsResponse> {
  const { data } =
    await bridgelessRestApiClient.get<UnbondingDelegationsResponse>(
      `/cosmos/staking/v1beta1/delegators/${delegatorAddress}/unbonding_delegations`,
      {
        params: buildPaginationParams(pagination),
      },
    )
  return data
}

export interface RedelegationsResponse {
  redelegation_responses: Array<{
    redelegation: {
      delegator_address: string
      validator_src_address: string
      validator_dst_address: string
    }
  }>
  pagination: PaginationResponse
}

export async function fetchRedelegations(
  delegatorAddress: string,
): Promise<RedelegationsResponse> {
  const { data } = await bridgelessRestApiClient.get<RedelegationsResponse>(
    `/cosmos/staking/v1beta1/delegators/${delegatorAddress}/redelegations`,
  )
  return data
}
